import { StateCreator } from "zustand";
import type { SkyViewStore } from "./index";

export type SearchMode = "exact" | "context";

export interface SearchHighlightSlice {
  searchQuery: string | null;
  highlightedPageId: string | null;
  highlightedSectionId: string | null;
  searchMode: SearchMode;
  referenceHighlightsEnabled: boolean;
  setSearchQuery: (query: string | null) => void;
  setHighlightedPageId: (pageId: string | null) => void;
  highlightReference: (
    pageId: string,
    context: string,
    sectionId?: string | null
  ) => void;
  clearSearchHighlight: () => void;
  setReferenceHighlightsEnabled: (enabled: boolean) => void;
}

type PageHighlightState = Pick<
  SearchHighlightSlice,
  | "searchQuery"
  | "highlightedPageId"
  | "highlightedSectionId"
  | "searchMode"
  | "referenceHighlightsEnabled"
>;

export const shouldFetchPageHighlights = (
  {
    searchQuery,
    highlightedPageId,
    highlightedSectionId,
    searchMode,
    referenceHighlightsEnabled,
  }: PageHighlightState,
  pageId: string,
  sectionId: string
) =>
  (referenceHighlightsEnabled || searchMode === "exact") &&
  !!searchQuery &&
  (searchMode === "context" && highlightedSectionId
    ? highlightedSectionId === sectionId
    : highlightedPageId === pageId);

export const createSearchHighlightSlice: StateCreator<
  SkyViewStore,
  [["zustand/persist", unknown], ["zustand/immer", never]],
  [],
  SearchHighlightSlice
> = (set) => ({
  searchQuery: null,
  highlightedPageId: null,
  highlightedSectionId: null,
  searchMode: "exact",
  referenceHighlightsEnabled: true,
  setSearchQuery: (query: string | null) =>
    set(() => ({
      searchQuery: query,
      searchMode: "exact",
    })),
  setHighlightedPageId: (pageId: string | null) =>
    set(() => ({
      highlightedPageId: pageId,
    })),
  highlightReference: (
    pageId: string,
    context: string,
    sectionId: string | null = null
  ) =>
    set(() => ({
      searchQuery: context,
      highlightedPageId: pageId,
      highlightedSectionId: sectionId,
      searchMode: "context",
    })),
  clearSearchHighlight: () =>
    set(() => ({
      searchQuery: null,
      highlightedPageId: null,
      highlightedSectionId: null,
      searchMode: "exact",
    })),
  setReferenceHighlightsEnabled: (enabled: boolean) =>
    set(() => ({
      referenceHighlightsEnabled: enabled,
    })),
});
